/**
 * Questionnaire fermé de personnalisation métier (onboarding FlashLevel / LILATE IA).
 *
 * Les valeurs sont le contrat API avec le moteur Rakam
 * (`user.personalization` de prepare-test, puis start-test) : uniquement des
 * réponses fermées. Toutes les questions du step personnalisation sont
 * obligatoires côté UI (taille d'organisation, interlocuteurs, management ;
 * taille d'équipe si manager).
 */

export interface PersonalizationAnswers {
    company_size: string | null;
    interlocutor_types: string[];
    is_manager: boolean | null;
    team_size: string | null;
    /**
     * Langue maternelle confirmée par le candidat (nom anglais Rakam,
     * ex. 'French'). Envoyée dans `user.native_language` du payload Rakam
     * (jamais dans `user.personalization`) et utilisée pour mettre à jour
     * `User.main_language` (profil Engage). Le moteur Rakam résout cette valeur
     * (supportée -> langue native, non supportée -> anglais, absente ->
     * français).
     */
    native_language: string | null;
}

export interface PersonalizationOption {
    value: string;
    labelKey: string;
}

/** Option de langue native : label = autonyme (pas de traduction nécessaire). */
export interface NativeLanguageOption {
    value: string;
    label: string;
}

export const COMPANY_SIZE_OPTIONS: PersonalizationOption[] = [
    { value: '1-10', labelKey: 'ai-exam-onboarding.personalization.company-size.1-10' },
    { value: '11-50', labelKey: 'ai-exam-onboarding.personalization.company-size.11-50' },
    { value: '51-250', labelKey: 'ai-exam-onboarding.personalization.company-size.51-250' },
    { value: '251-1000', labelKey: 'ai-exam-onboarding.personalization.company-size.251-1000' },
    { value: '1000+', labelKey: 'ai-exam-onboarding.personalization.company-size.1000+' },
];

/** Option taille d'organisation affichée sur l'écran univers professionnel (maquette 6). */
export interface CompanySizeDisplayOption extends PersonalizationOption {
    icon: string;
    subLabelKey: string;
}

/** 3 tailles simplifiées mappées sur les valeurs API Rakam. */
export const COMPANY_SIZE_DISPLAY_OPTIONS: CompanySizeDisplayOption[] = [
    {
        value: '11-50',
        icon: '🏠',
        labelKey: 'ai-exam-onboarding.professional-universe.company-size.small',
        subLabelKey: 'ai-exam-onboarding.professional-universe.company-size.small-sub',
    },
    {
        value: '251-1000',
        icon: '🏢',
        labelKey: 'ai-exam-onboarding.professional-universe.company-size.medium',
        subLabelKey: 'ai-exam-onboarding.professional-universe.company-size.medium-sub',
    },
    {
        value: '1000+',
        icon: '🏙️',
        labelKey: 'ai-exam-onboarding.professional-universe.company-size.large',
        subLabelKey: 'ai-exam-onboarding.professional-universe.company-size.large-sub',
    },
];

/** Interlocuteurs proposés sur l'écran univers professionnel FlashLevel (maquette 6). */
export const FLASHLEVEL_INTERLOCUTOR_OPTIONS: PersonalizationOption[] = [
    { value: 'colleagues', labelKey: 'ai-exam-onboarding.professional-universe.interlocutors.colleagues' },
    { value: 'clients', labelKey: 'ai-exam-onboarding.professional-universe.interlocutors.clients' },
    { value: 'management', labelKey: 'ai-exam-onboarding.professional-universe.interlocutors.management' },
    { value: 'suppliers_contractors', labelKey: 'ai-exam-onboarding.professional-universe.interlocutors.suppliers' },
    { value: 'international_partners', labelKey: 'ai-exam-onboarding.professional-universe.interlocutors.international' },
    { value: 'general_public', labelKey: 'ai-exam-onboarding.professional-universe.interlocutors.public' },
];

/** Secteurs d'activité proposés en liste fermée (maquette 6). */
export const ACTIVITY_SECTOR_OPTIONS: PersonalizationOption[] = [
    { value: 'banking_finance', labelKey: 'ai-exam-onboarding.professional-universe.sector.banking-finance' },
    { value: 'retail', labelKey: 'ai-exam-onboarding.professional-universe.sector.retail' },
    { value: 'education', labelKey: 'ai-exam-onboarding.professional-universe.sector.education' },
    { value: 'hospitality', labelKey: 'ai-exam-onboarding.professional-universe.sector.hospitality' },
    { value: 'manufacturing', labelKey: 'ai-exam-onboarding.professional-universe.sector.manufacturing' },
    { value: 'tech', labelKey: 'ai-exam-onboarding.professional-universe.sector.tech' },
    { value: 'luxury_fashion', labelKey: 'ai-exam-onboarding.professional-universe.sector.luxury-fashion' },
    { value: 'healthcare', labelKey: 'ai-exam-onboarding.professional-universe.sector.healthcare' },
    { value: 'logistics', labelKey: 'ai-exam-onboarding.professional-universe.sector.logistics' },
    { value: 'insurance', labelKey: 'ai-exam-onboarding.professional-universe.sector.insurance' },
    { value: 'real_estate', labelKey: 'ai-exam-onboarding.professional-universe.sector.real-estate' },
    { value: 'media', labelKey: 'ai-exam-onboarding.professional-universe.sector.media' },
    { value: 'public_sector', labelKey: 'ai-exam-onboarding.professional-universe.sector.public-sector' },
    { value: 'other', labelKey: 'ai-exam-onboarding.professional-universe.sector.other' },
];

export const INTERLOCUTOR_TYPE_OPTIONS: PersonalizationOption[] = [
    { value: 'clients', labelKey: 'ai-exam-onboarding.personalization.interlocutors.clients' },
    { value: 'colleagues', labelKey: 'ai-exam-onboarding.personalization.interlocutors.colleagues' },
    { value: 'suppliers_contractors', labelKey: 'ai-exam-onboarding.personalization.interlocutors.suppliers-contractors' },
    { value: 'management', labelKey: 'ai-exam-onboarding.personalization.interlocutors.management' },
    { value: 'managed_team', labelKey: 'ai-exam-onboarding.personalization.interlocutors.managed-team' },
    { value: 'international_partners', labelKey: 'ai-exam-onboarding.personalization.interlocutors.international-partners' },
    // Catégories sectorielles (questionnaire V2) : métiers santé, éducation
    // et accueil du public, mal couverts par la liste corporate d'origine.
    { value: 'patients', labelKey: 'ai-exam-onboarding.personalization.interlocutors.patients' },
    { value: 'students_trainees', labelKey: 'ai-exam-onboarding.personalization.interlocutors.students-trainees' },
    { value: 'general_public', labelKey: 'ai-exam-onboarding.personalization.interlocutors.general-public' },
    { value: 'other', labelKey: 'ai-exam-onboarding.personalization.interlocutors.other' },
];

export const TEAM_SIZE_OPTIONS: PersonalizationOption[] = [
    { value: '1-5', labelKey: 'ai-exam-onboarding.personalization.team-size.1-5' },
    { value: '6-20', labelKey: 'ai-exam-onboarding.personalization.team-size.6-20' },
    { value: '20+', labelKey: 'ai-exam-onboarding.personalization.team-size.20+' },
];

/**
 * Langues proposées pour la confirmation de langue maternelle.
 * Valeur = nom anglais attendu par le moteur Rakam ; label = autonyme
 * (chaque locuteur reconnaît sa langue sans traduction).
 */
export const NATIVE_LANGUAGE_OPTIONS: NativeLanguageOption[] = [
    { value: 'French', label: 'Français' },
    { value: 'English', label: 'English' },
    { value: 'Spanish', label: 'Español' },
    { value: 'Italian', label: 'Italiano' },
    { value: 'German', label: 'Deutsch' },
    { value: 'Portuguese', label: 'Português' },
    { value: 'Dutch', label: 'Nederlands' },
    { value: 'Arabic', label: 'العربية' },
    { value: 'Chinese', label: '中文' },
    { value: 'Russian', label: 'Русский' },
    { value: 'Japanese', label: '日本語' },
    { value: 'Turkish', label: 'Türkçe' },
    { value: 'Polish', label: 'Polski' },
    { value: 'Romanian', label: 'Română' },
    { value: 'Ukrainian', label: 'Українська' },
];

export function emptyPersonalizationAnswers(): PersonalizationAnswers {
    return {
        company_size: null,
        interlocutor_types: [],
        is_manager: null,
        team_size: null,
        native_language: null,
    };
}

/** Codes ISO (langue UI / code Language Engage sans préfixe) → nom anglais Rakam. */
const ISO_TO_RAKAM_LANGUAGE: Record<string, string> = {
    fr: 'French',
    en: 'English',
    es: 'Spanish',
    it: 'Italian',
    de: 'German',
    pt: 'Portuguese',
    nl: 'Dutch',
    ar: 'Arabic',
    zh: 'Chinese',
    ru: 'Russian',
    ja: 'Japanese',
    tr: 'Turkish',
    pl: 'Polish',
    ro: 'Romanian',
    uk: 'Ukrainian',
};

/**
 * Langue native probable du candidat à partir de la langue d'interface,
 * au format attendu par le moteur Rakam (`user.native_language`).
 */
export function nativeLanguageFromUiLang(uiLang: string | null): string {
    return ISO_TO_RAKAM_LANGUAGE[uiLang || ''] || 'French';
}

/**
 * Langue native à partir du code d'une Language Engage (`'language.fr'`),
 * utilisée pour préremplir la confirmation depuis User.main_language.
 * Retourne null si le code n'est pas mappé.
 */
export function nativeLanguageFromEngageCode(code: string | null): string | null {
    if (!code) {
        return null;
    }
    const iso = code.replace(/^language\./, '');
    return ISO_TO_RAKAM_LANGUAGE[iso] || null;
}

/**
 * Code de Language Engage (`'language.fr'`) correspondant à un nom anglais
 * Rakam, pour mettre à jour User.main_language après confirmation.
 * Retourne null si la langue n'est pas mappée.
 */
export function engageCodeFromNativeLanguage(name: string | null): string | null {
    if (!name) {
        return null;
    }
    const entry = Object.entries(ISO_TO_RAKAM_LANGUAGE).find(([, v]) => v === name);
    return entry ? `language.${entry[0]}` : null;
}

/**
 * Indique si le questionnaire de personnalisation métier est entièrement
 * renseigné (bloque le bouton Continuer des écrans d'onboarding).
 */
export function isPersonalizationComplete(answers: PersonalizationAnswers | null): boolean {
    if (!answers?.company_size) {
        return false;
    }
    if (answers.interlocutor_types.length === 0) {
        return false;
    }
    if (answers.is_manager === null) {
        return false;
    }
    if (answers.is_manager === true && !answers.team_size) {
        return false;
    }
    return true;
}

/**
 * Construit le bloc `user.personalization` du payload prepare-test / start-test.
 * Retourne null tant que la taille d'organisation n'est pas renseignée ;
 * les champs non renseignés sont omis du payload.
 */
export function buildPersonalizationPayload(
    answers: PersonalizationAnswers | null
): Record<string, unknown> | null {
    if (!answers || !answers.company_size) {
        return null;
    }
    const payload: Record<string, unknown> = { company_size: answers.company_size };
    if (answers.interlocutor_types.length > 0) {
        payload['interlocutor_types'] = answers.interlocutor_types;
    }
    if (answers.is_manager !== null) {
        payload['is_manager'] = answers.is_manager;
        // La taille d'équipe n'a de sens que pour un manager déclaré.
        if (answers.is_manager && answers.team_size) {
            payload['team_size'] = answers.team_size;
        }
    }
    return payload;
}
